<template>
  <div class="similar-cruises">
    
    <div v-if="loading" class="loading-indicator">
      <div class="spinner-sm"></div>
      <span>Finding similar cruises...</span>
    </div>
    
    <div v-else-if="error" class="error-message">
      <i class="fas fa-exclamation-circle"></i>
      <span>{{ error }}</span>
    </div>
    
    <div v-else-if="visibleCruises.length === 0" class="no-results">
      <i class="fas fa-search"></i>
      <p>No similar cruises found at this time.</p>
    </div>
    
    <!-- CP-238: each card is a real link (Tab reaches it, Enter opens it, Space too). Its accessible
         name is its visible text (title, port, date, nights, price), so it is descriptive and passes
         axe label-content-name-mismatch; decorative icons are aria-hidden. -->
    <ul v-else class="cruise-recommendations" role="list">
      <li v-for="cruise in visibleCruises" :key="cruise.id" class="recommendation-item">
      <a
        class="recommendation-card"
        :href="recommendationHref(cruise)"
        @click="onCardClick($event, cruise)"
        @keydown.space.prevent="navigateToCruise(cruise.id)"
      >
        <div class="recommendation-image">
          <div class="image-placeholder">
            <i class="fas fa-ship" aria-hidden="true"></i>
          </div>
          <div v-if="isSameShip(cruise)" class="same-ship-badge">
            Same Ship
          </div>
        </div>
        
        <div class="recommendation-details">
          <h4 class="cruise-name">{{ formatPlaceTitle(cruise.name) }}</h4>
          <div class="cruise-info">
            <div class="info-item">
              <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
              <span>{{ formatPlaceTitle(cruise.departure_port) }}</span>
            </div>
            <div class="info-item">
              <i class="fas fa-calendar-alt" aria-hidden="true"></i>
              <span>{{ formatDate(cruise.departure_date) }}</span>
            </div>
            <div v-if="nightsText(cruise.duration)" class="info-item">
              <i class="fas fa-moon" aria-hidden="true"></i>
              <span>{{ nightsText(cruise.duration) }}</span>
            </div>
          </div>
          
          <div class="price-container">
            <template v-if="showsListingPrice(cruise)">
              <span class="price-label">From</span>
              <span class="price-value">${{ formatPriceAmount(cruise.current_price) }}</span>
              <span v-if="listingCabinLabel(cruise)" class="cabin-type">({{ listingCabinLabel(cruise) }})</span>
              <span class="price-freshness">{{ listingPriceCue(cruise) }}</span>
            </template>
            <template v-else>
              <span class="price-unavailable">{{ hiddenPriceReason(cruise) }}</span>
              <span v-if="staleAgeStamp(cruise)" class="price-freshness">{{ staleAgeStamp(cruise) }}</span>
            </template>
          </div>
        </div>
      </a>
      </li>
    </ul>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/services/api';
import { hiddenPriceReason, listingCabinLabel, listingPriceCue, showsListingPrice, staleAgeStamp } from '@/utils/priceFreshness';
import { includeRecommendation, orderRecommendationCards, showSameShipBadge } from '@/utils/unpublishedShell';

import { formatPriceAmount } from '@/utils/formatMoney';
import { formatPlaceTitle } from '@/utils/formatPlaceTitle';
import { isPlainActivation, recommendationHref } from '@/utils/detailKeyboard';
export default {
  name: 'SimilarCruises',
  props: {
    cruiseId: {
      type: [Number, String],
      required: true,
      validator: function(value) {
        // Make sure we have a valid cruise ID
        return value !== undefined && value !== null && value !== '';
      }
    },
    currentCruise: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const router = useRouter();
    const similarCruises = ref([]);
    const loading = ref(true);
    const error = ref(null);
    
    const fetchSimilarCruises = async () => {
      if (!props.cruiseId) {
        console.error('No cruise ID provided to SimilarCruises component');
        error.value = 'Missing cruise ID';
        return;
      }
      
      loading.value = true;
      error.value = null;
      
      try {
        const response = await api.getSimilarCruises(props.cruiseId);
        similarCruises.value = response.data;
      } catch (err) {
        console.error('Error fetching similar cruises:', err);
        error.value = 'Unable to load similar cruises';
      } finally {
        loading.value = false;
      }
    };
    
    const visibleCruises = computed(() => orderRecommendationCards(
      similarCruises.value.filter((cruise) => includeRecommendation(props.currentCruise, cruise)),
      showsListingPrice,
      listingCabinLabel,
      props.currentCruise,
    ));

    const isSameShip = (cruise) => showSameShipBadge(props.currentCruise, cruise);

    const nightsText = (duration) => {
      const nights = Number(duration);
      if (!Number.isFinite(nights) || nights <= 0) return '';
      const rounded = Math.round(nights);
      if (rounded <= 0) return '';
      return rounded === 1 ? '1 Night' : `${rounded} Nights`;
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return 'Date TBD';
      
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    };
    
    const navigateToCruise = (id) => {
      router.push(`/cruise/${id}`);
    };

    // Plain click / Enter: SPA navigation. Ctrl/Cmd/Shift/middle click: let the browser open the href.
    const onCardClick = (event, cruise) => {
      if (!isPlainActivation(event)) return;
      event.preventDefault();
      navigateToCruise(cruise.id);
    };
    
    onMounted(() => {
      console.log('SimilarCruises component mounted with cruise ID:', props.cruiseId);
      // Add a small delay to ensure the component is fully mounted
      setTimeout(() => {
        fetchSimilarCruises();
      }, 100);
    });
    
    // Refetch when cruise ID changes
    watch(() => props.cruiseId, (newId, oldId) => {
      console.log(`Cruise ID changed from ${oldId} to ${newId}`);
      if (newId) {
        fetchSimilarCruises();
      }
    }, { immediate: true });
    
    return {
      formatPlaceTitle,
      formatPriceAmount,
      similarCruises,
      visibleCruises,
      loading,
      error,
      isSameShip,
      nightsText,
      formatDate,
      navigateToCruise,
      onCardClick,
      recommendationHref,
      showsListingPrice,
      listingCabinLabel,
      listingPriceCue,
      staleAgeStamp,
      hiddenPriceReason
    };
  }
}
</script>

<style scoped>
.similar-cruises {
  margin-top: 20px;
}

.section-title {
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 16px;
  color: var(--cs-d-text);
}

.loading-indicator, .error-message, .no-results {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: var(--cs-d-surface-alt);
  border-radius: 8px;
  color: var(--cs-d-muted);
}

.loading-indicator {
  gap: 12px;
}

.spinner-sm {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid var(--cs-d-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.error-message, .no-results {
  flex-direction: column;
  gap: 8px;
}

.error-message i, .no-results i {
  font-size: 24px;
  color: var(--cs-d-error);
  margin-bottom: 8px;
}

.no-results i {
  color: var(--cs-d-muted);
}

.cruise-recommendations {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.recommendation-item {
  display: flex;
  min-width: 0;
}

.recommendation-card {
  display: block;
  width: 100%;
  color: inherit;
  text-decoration: none;
  background-color: var(--cs-d-surface);
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.recommendation-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* CP-238: visible keyboard focus, same ring as the other Detail controls (--cs-signal-teal). */
.recommendation-card:focus-visible {
  outline: 2px solid var(--cs-signal-teal);
  outline-offset: 2px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.recommendation-image {
  position: relative;
  height: 120px;
  background-color: var(--cs-d-tint);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.image-placeholder i {
  font-size: 36px;
  color: var(--cs-d-blue);
}

.same-ship-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: var(--cs-price-fill);
  color: white;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
}

.recommendation-details {
  padding: 16px;
}

.cruise-name {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--cs-d-link);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cruise-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--cs-d-muted);
}

.info-item i {
  width: 16px;
  color: var(--cs-d-muted);
}

.price-container {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  margin-top: 12px;
}

.price-freshness {
  flex-basis: 100%;
  font-size: 12px;
  line-height: 1.35;
  color: var(--cs-d-muted);
}

.price-unavailable {
  font-size: 14px;
  font-weight: 500;
  font-style: italic;
  color: var(--cs-d-muted);
}

.price-label {
  font-size: 12px;
  color: var(--cs-d-muted);
}

.price-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--cs-price);
}

.cabin-type {
  font-size: 0.75rem;
  color: var(--cs-muted);
  font-weight: normal;
  font-style: italic;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .cruise-recommendations {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }
}

@media (max-width: 480px) {
  .cruise-recommendations {
    grid-template-columns: 1fr;
  }
}
</style>
